<template>
  <!-- 接口环境悬浮切换器（内部调试用，全局挂在 App.vue 上）
       只在"可切换"宿主显示：本地开发 / pcmall-test / test-pc / 192.168.110.*；
       正式域名永远走 prod —— 这里不渲染，也不会写任何覆盖值。
       切档会写入 localStorage 并整页重载（见 src/config/api.js 的 setApiEnv）。
       胶囊按住可拖动，位置记在 localStorage（换页/刷新不丢） -->
  <div
    class="apiEnvSwitch"
    :style="rootStyle"
    :class="{ dragging }"
    v-if="switchable"
  >
    <div class="panel" v-if="open" :style="panelStyle">
      <p class="title">切换环境（自动刷新）</p>
      <ul class="list">
        <li
          v-for="item in envs"
          :key="item.value"
          :class="['item', { on: item.value === env }]"
          @click="pick(item.value)"
        >
          <span>{{ item.label }}</span
          ><em>{{ item.value }}</em>
        </li>
        <li class="item reset" v-if="allowReset" @click="pick('reset')">
          <span>回默认档</span><em>reset</em>
        </li>
      </ul>
      <p class="title">当前各服务地址</p>
      <ul class="list urls">
        <li v-for="s in services" :key="s.name">
          <b>{{ s.label }}</b
          ><span>{{ baseURLOf(s.name) || '（同源）' }}</span>
        </li>
      </ul>
    </div>
    <!-- 拖动与点击分开：位移超过 4px 视为拖动，不触发面板开合
         （与频道栏 SwitchChannel / 分类 tab cateTabs 同一口径） -->
    <div
      ref="pill"
      class="pill"
      :class="'env-' + env"
      title="按住可拖动，单击展开"
      @pointerdown="onPointerDown"
    >
      <i class="dot"></i>
      <span>接口：{{ labelOf(env) }}</span>
      <i class="caret" :class="{ up: open }"></i>
    </div>
  </div>
</template>

<script>
import {
  canSwitchApiEnv,
  getSelectableEnvs,
  getServiceBaseURL,
  resolveApiEnv,
  setApiEnv
} from '@/config/api';

const LABELS = { prod: '正式', pre: '预发布', test: '测试', local: '本地' };
const SERVICES = [
  { name: 'main', label: '主接口' },
  { name: 'op', label: 'op' },
  { name: 'movie', label: '电影' },
  { name: 'tour', label: '景点' }
];

const POS_KEY = 'pc_api_switch_pos';
/** 拖动判定阈值：位移超过它就不算点击（与频道栏 / 分类 tab 同一口径） */
const DRAG_GAP = 4;
/** 面板宽度（与样式里的 .panel width 保持一致），用于靠边时对齐 */
const PANEL_WIDTH = 300;
/** 面板高度的估算值：只用来判断"上方放不放得下"，放不下就翻到胶囊下面 */
const PANEL_HEIGHT = 200;

/** 读上次拖到的位置；没有或格式不对就用默认（右下角，见样式） */
function readPos() {
  try {
    const pos = JSON.parse(window.localStorage.getItem(POS_KEY) || 'null');
    if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
      return { x: pos.x, y: pos.y };
    }
  } catch (err) {
    /* 解析失败按"没拖过"处理 */
  }
  return null;
}

export default {
  name: 'ApiEnvSwitch',
  data() {
    return {
      switchable: false,
      open: false,
      env: 'prod',
      services: SERVICES,
      // 拖动后的位置（视口坐标，锚点是胶囊左上角）；null = 用样式的默认右下角
      pos: null,
      dragging: false,
      dragMoved: false,
      dragStartX: 0,
      dragStartY: 0,
      dragOffsetX: 0,
      dragOffsetY: 0
    };
  },
  computed: {
    // 可选项由 config/api 按宿主给：测试站点不展示「本地」与「回默认档」
    selectable() {
      return getSelectableEnvs();
    },
    envs() {
      return this.selectable
        .filter(value => value !== 'reset')
        .map(value => ({ value, label: LABELS[value] || value }));
    },
    allowReset() {
      return this.selectable.indexOf('reset') !== -1;
    },
    rootStyle() {
      if (!this.pos) return null;
      return {
        left: `${this.pos.x}px`,
        top: `${this.pos.y}px`,
        right: 'auto',
        bottom: 'auto'
      };
    },
    // 面板默认贴在胶囊上方、左对齐；两侧放不下就翻转，避免被挤出视口。
    // 注意不能用 this.pos 判断 —— 没拖过时 pos 是 null（默认停靠右下角），
    // 这时 300px 宽的面板从胶囊左边缘往右铺，必然超出屏幕右侧。
    panelStyle() {
      const el = this.$el;
      const rect = el ? el.getBoundingClientRect() : null;
      const style = {};
      // 右侧放不下 → 改成右对齐（默认右下角停靠就属于这种情况）
      const left = this.pos ? this.pos.x : rect ? rect.left : 0;
      if (left + PANEL_WIDTH > window.innerWidth) {
        style.left = 'auto';
        style.right = '0';
      }
      // 上方放不下 → 改到胶囊下方
      const top = this.pos ? this.pos.y : rect ? rect.top : 0;
      if (top < PANEL_HEIGHT) {
        style.top = 'calc(100% + 8px)';
        style.bottom = 'auto';
      }
      return style;
    }
  },
  created() {
    this.switchable = canSwitchApiEnv();
    this.env = resolveApiEnv();
    this.pos = readPos();
  },
  mounted() {
    window.addEventListener('resize', this.clampPos);
    this.$nextTick(this.clampPos);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.clampPos);
    this.stopDrag();
  },
  methods: {
    labelOf(value) {
      return LABELS[value] || value;
    },
    baseURLOf(service) {
      return getServiceBaseURL(service);
    },
    pick(value) {
      // 已经在这一档就不用重载了
      if (value !== 'reset' && value === this.env) return;
      setApiEnv(value);
    },
    onPointerDown(e) {
      // 只处理左键 / 触摸
      if (e.button !== undefined && e.button !== 0) return;
      // 已经在拖动中（多点触摸）忽略后进来的
      if (this.dragging) return;
      const rect = this.$el.getBoundingClientRect();
      // 首次拖动：先把"默认右下角"固化成 left/top，否则会从 0,0 跳过来
      if (!this.pos) this.pos = { x: rect.left, y: rect.top };
      this.dragging = true;
      this.dragMoved = false;
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.dragOffsetX = e.clientX - rect.left;
      this.dragOffsetY = e.clientY - rect.top;
      // 挂在 window 上：指针拖出胶囊也不中断
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
      // 阻止拖动时选中文字 / 触发原生拖放
      e.preventDefault();
    },
    onPointerMove(e) {
      if (!this.dragging) return;
      if (!this.dragMoved) {
        const dx = Math.abs(e.clientX - this.dragStartX);
        const dy = Math.abs(e.clientY - this.dragStartY);
        if (dx > DRAG_GAP || dy > DRAG_GAP) this.dragMoved = true;
      }
      this.pos = {
        x: e.clientX - this.dragOffsetX,
        y: e.clientY - this.dragOffsetY
      };
      this.clampPos();
    },
    onPointerUp() {
      if (!this.dragging) return;
      this.dragging = false;
      this.stopDrag();
      if (this.dragMoved) {
        this.savePos();
      } else {
        // 没拖动 = 单击：开合面板
        this.open = !this.open;
      }
    },
    stopDrag() {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    },
    // 不允许把胶囊拖出视口（面板在胶囊上方，可以溢出）
    clampPos() {
      if (!this.pos) return;
      const el = this.$refs.pill;
      const w = el ? el.offsetWidth : 120;
      const h = el ? el.offsetHeight : 28;
      this.pos = {
        x: Math.min(
          Math.max(0, this.pos.x),
          Math.max(0, window.innerWidth - w)
        ),
        y: Math.min(
          Math.max(0, this.pos.y),
          Math.max(0, window.innerHeight - h)
        )
      };
    },
    savePos() {
      try {
        window.localStorage.setItem(POS_KEY, JSON.stringify(this.pos));
      } catch (err) {
        /* 隐私模式等：忽略，只是记不住位置 */
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.apiEnvSwitch {
  position: fixed;
  right: 16px;
  bottom: 90px;
  // 高于头部（999）与吸顶遮罩（1000），任何页面都能点到
  z-index: 10001;
  font-size: 12px;
  line-height: 1.6;
  color: #333;
  text-align: left;
  &.dragging {
    user-select: none;
  }
  .pill {
    display: flex;
    align-items: center;
    // 显式行高 + 不换行：别和外部 1.6 的行高混算，配合 flex 居中文字才不偏
    line-height: 1;
    white-space: nowrap;
    height: 28px;
    padding: 0 10px;
    border-radius: 14px;
    background: rgba(0, 0, 0, 0.72);
    color: #fff;
    cursor: grab;
    user-select: none;
    // 拖动时不要触发页面滚动（移动端）
    touch-action: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    .dot {
      width: 6px;
      height: 6px;
      margin-right: 6px;
      border-radius: 50%;
      background: #52c41a;
    }
    .caret {
      width: 0;
      height: 0;
      margin-left: 6px;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      border-top: 4px solid currentColor;
      transition: transform 0.15s;
      &.up {
        transform: rotate(180deg);
      }
    }
    // 一眼能看出在哪个环境
    &.env-prod .dot {
      background: #ff4d4f;
    }
    &.env-pre .dot {
      background: #faad14;
    }
    &.env-test .dot {
      background: #1890ff;
    }
    &.env-local .dot {
      background: #52c41a;
    }
  }
  &.dragging .pill {
    cursor: grabbing;
  }
  // 面板绝对定位到胶囊上方：这样打开/关闭面板不会带动胶囊位移，
  // 拖动锚点始终是胶囊左上角
  .panel {
    position: absolute;
    left: 0;
    bottom: calc(100% + 8px);
    width: 300px; // 与脚本里的 PANEL_WIDTH 保持一致
    padding: 10px 12px;
    border-radius: 6px;
    background: #fff;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
    // 面板里所有文字用同一行高，避免继承外层 1.6 后与图标/标签对不齐
    line-height: 18px;
    .title {
      margin: 0 0 4px;
      color: #999;

      & + .list {
        margin-bottom: 8px;
      }
    }
    .list {
      margin: 0;
      padding: 0;
      list-style: none;
      li {
        display: flex;
        align-items: center;
        justify-content: space-between;
        // 标签与值之间留固定间距，不靠空格/换行撑
        gap: 12px;
        line-height: 18px;
        padding: 3px 6px;
        border-radius: 4px;
        em {
          flex: 0 0 auto;
          color: #bbb;
          font-style: normal;
        }
      }
    }
    .item {
      cursor: pointer;
      &:hover {
        background: #f4f5f7;
      }
      &.on {
        background: #e8f4ff;
        color: #1890ff;
        em {
          color: #1890ff;
        }
      }
      &.reset {
        margin-top: 2px;
        border-top: 1px dashed #eee;
      }
    }
    .urls {
      li {
        // 地址单独一列：长地址换行后仍对齐在标签右侧，不会回到行首
        display: flex;
        align-items: flex-start;
        padding: 2px 6px;
        color: #666;
        b {
          flex: 0 0 48px;
          color: #333;
        }
        span {
          flex: 1 1 auto;
          min-width: 0;
          word-break: break-all;
        }
      }
    }
  }
}
</style>
